/**
 * Per-buyer Data Encryption Key (DEK) access for the SPA.
 *
 * The DEK is the crypto password for every one of this buyer's IndexedDB
 * blobs (OAuth tokens, schedule connection mirrors). The license worker
 * mints it at signup, wraps it at rest in KV, and returns the plaintext to
 * the authenticated browser via /license/verify and /license/me.
 *
 * Lifecycle:
 *   - On magic-link verify: VerifyMagicLink caches it via setDek()
 *   - On a cold page load (valid JWT, empty cache): getDek() lazily fetches
 *     it from /license/me using the session JWT
 *   - On logout: clearDek() wipes it
 *
 * Stored in sessionStorage so it survives a refresh within the tab session
 * but not a full browser/tab close — at which point a fresh /license/me call
 * (gated by the still-valid JWT) re-fetches it.
 */

import { getSession } from "./session.js";
import { getMe } from "./license-api.js";

const DEK_KEY = "agencylens:dek";

let cached: string | undefined;

export function setDek(dek: string): void {
  cached = dek;
  try {
    sessionStorage.setItem(DEK_KEY, dek);
  } catch {
    /* sessionStorage unavailable — in-memory only */
  }
}

export function clearDek(): void {
  cached = undefined;
  try {
    sessionStorage.removeItem(DEK_KEY);
  } catch {
    /* ignore */
  }
}

function readCached(): string | undefined {
  if (cached) return cached;
  try {
    const v = sessionStorage.getItem(DEK_KEY);
    if (v) {
      cached = v;
      return v;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * Returns the buyer's DEK. Uses the cached value when present; otherwise
 * fetches it from /license/me with the session JWT and caches it. Throws if
 * there is no active session or the worker can't return a DEK.
 */
export async function getDek(): Promise<string> {
  const c = readCached();
  if (c) return c;
  const session = getSession();
  if (!session) throw new Error("not_authenticated");
  const { dek } = await getMe(session.jwt);
  if (!dek) throw new Error("dek_unavailable");
  setDek(dek);
  return dek;
}
